import baseUrl from "../../../api/baseUrl";
import { appointmentIdPayload } from "../../../utils/appointmentLinkContext";
import {
  validateTreatmentForm,
  type TreatmentFormData,
} from "../treatmenttable/components/treatmentform/treatmentFormUtils";
import {
  validatePrescriptionForm,
  type PrescriptionFormData,
} from "../prescription/prescriptionFormUtils";
import {
  PLAN_CATEGORIES,
  isPlanFormPending,
  type DraftRow,
  type PlanCategory,
  type PlanFormState,
} from "./treatmentDraftStore";

/**
 * Save unsaved rows straight from the draft store, so the shell can save a
 * tab's rows while that tab is not mounted. Every row posts on its own, as
 * the per-tab buttons did. A failed row is reported by id with its message;
 * the caller keeps it as a draft and drops the rest.
 */

export const DEFAULT_MAIN_TREATMENT_STATUS = "Complete";

export interface SaveRowsResult {
  savedIds: string[];
  errors: Record<string, string>;
}

const errorMessage = (error: any, fallback: string): string =>
  error?.response?.data?.code === "INSUFFICIENT_STOCK"
    ? error?.response?.data?.message || "Insufficient stock to dispense."
    : error?.response?.data?.message || fallback;

export async function saveTreatmentDraftRows(
  treatmentId: string,
  appointmentId: string,
  rows: DraftRow<TreatmentFormData>[]
): Promise<SaveRowsResult> {
  const result: SaveRowsResult = { savedIds: [], errors: {} };
  if (!treatmentId || rows.length === 0) return result;

  let catalog: any[] = [];
  try {
    const response = await baseUrl.get("alltreatment/findAllTreatment");
    catalog = response.data.data ?? [];
  } catch (error) {
    console.error(error);
    rows.forEach((row) => {
      result.errors[row.id] = "Failed to load treatment catalog";
    });
    return result;
  }

  for (const row of rows) {
    const validation = validateTreatmentForm(
      row.data,
      catalog,
      false,
      DEFAULT_MAIN_TREATMENT_STATUS
    );
    if (!validation.valid) {
      result.errors[row.id] = validation.message;
      continue;
    }
    try {
      await baseUrl.post(
        "treatment/add-treatments",
        { ...validation.payload, ...appointmentIdPayload(appointmentId) },
        { headers: { treatmentid: treatmentId } }
      );
      result.savedIds.push(row.id);
    } catch (error: any) {
      result.errors[row.id] = errorMessage(error, "Failed to add treatment");
    }
  }
  return result;
}

export interface SavedPrescriptionItem {
  id: string;
  item: Record<string, any>;
}

export async function savePrescriptionDraftRows(
  treatmentId: string,
  rows: DraftRow<PrescriptionFormData>[]
): Promise<SaveRowsResult & { saved: SavedPrescriptionItem[] }> {
  const result = { savedIds: [] as string[], errors: {} as Record<string, string>, saved: [] as SavedPrescriptionItem[] };
  if (!treatmentId || rows.length === 0) return result;

  for (const row of rows) {
    const validation = validatePrescriptionForm(row.data);
    if (!validation.valid) {
      result.errors[row.id] = validation.message;
      continue;
    }
    try {
      const response = await baseUrl.patch(
        "/client/treatments/prescription/addprescription",
        validation.payload,
        { headers: { treatmentid: treatmentId } }
      );
      result.savedIds.push(row.id);
      result.saved.push({
        id: row.id,
        item: {
          ...validation.payload,
          createdAt: response.data.data.createdAt,
          _id: response.data.data._id,
          drugs: validation.payload.drugs,
          dose: validation.payload.dose || validation.payload.doseId,
        },
      });
    } catch (error: any) {
      result.errors[row.id] = errorMessage(error, "Failed to save prescription");
    }
  }
  return result;
}

export interface SavePlanResult {
  savedCategories: PlanCategory[];
  errors: Partial<Record<PlanCategory, string>>;
}

export async function savePlanForms(
  treatmentId: string,
  plan: Record<PlanCategory, PlanFormState>
): Promise<SavePlanResult> {
  const result: SavePlanResult = { savedCategories: [], errors: {} };
  if (!treatmentId) return result;

  for (const category of PLAN_CATEGORIES) {
    const form = plan[category];
    if (!isPlanFormPending(form)) continue;
    if (!form.option.trim()) {
      result.errors[category] = "Pick a treatment for the note";
      continue;
    }
    try {
      await baseUrl.post(
        "treatment/add-treatment-plan-item",
        { category, optionLabel: form.option, note: form.note.trim() },
        { headers: { treatmentid: treatmentId } }
      );
      result.savedCategories.push(category);
    } catch (error: any) {
      result.errors[category] = errorMessage(error, "Failed to add plan item");
    }
  }
  return result;
}
